import React, { useState, useRef } from 'react';
import { Alert } from 'reactstrap';
import type { Customer } from '../../../../../models/companies/kyccs/kycc';
import type { Condition } from '../../../../../models/companies/kyccs/condition';
import { defaultCondition } from '../../../../../models/companies/kyccs/condition';
import { KyccClient } from '../../../../../models/companies/kyccs/kyccClient';
import { Conditions } from './conditions';
import { Checking } from '../checking';
import Results from './results';
import { validate } from '../../../../../models/companies/kyccs/kycc';
import { handleAxiosError } from '../../../../../../common/helpers/axiosHelper';

const Status = {
  start: 0,
  cheking: 1,
  done: 2,
};

interface Props {
  target: number;
  kyccMonthlyLimit: number;
  kyccCheckedCount: number;
}

export default function ShowView(props: Props) {
  const { target, kyccMonthlyLimit, kyccCheckedCount } = props;
  const kyccClient = useRef(new KyccClient());

  const [customer, setCustomer] = useState<Customer>({
    id: null,
    companyId: null,
    conditions: [defaultCondition],
    kyccMonthlyLimit: kyccMonthlyLimit,
    kyccCheckedCount: kyccCheckedCount,
    errors: {},
  });
  const [status, setStatus] = useState<number>(Status.start);

  const onAddCondition = () => {
    setCustomer(prev => ({
      ...prev,
      conditions: [...prev.conditions, defaultCondition],
    }));
  };

  const onChangeCondition = (index: number, condition: Condition) => {
    setCustomer(prev => ({
      ...prev,
      conditions: prev.conditions.map((item, i) =>
        i === index ? condition : item,
      ),
    }));
  };

  const onRemoveCondition = (index: number) => {
    setCustomer(prev => ({
      ...prev,
      conditions: prev.conditions.filter((_, i) => i !== index),
    }));
  };

  const onStartCheck = () => {
    if (!validate(customer)) {
      setCustomer(prev => ({
        ...prev,
        conditions: [...prev.conditions],
      }));
      return;
    }

    setStatus(Status.cheking);
    kyccClient.current
      .checkCustomer(target, customer)
      .then(response => {
        setCustomer(prev => ({ ...prev, ...response.data }));
        setStatus(Status.done);
      })
      .catch(error => {
        const updatedCustomer = { ...customer };
        if (error.code === 'ECONNABORTED') {
          updatedCustomer.errors.base = [
            '接続がタイムアウトしました。検索結果が多すぎる可能性があります。条件を絞ってお試しください。',
          ];
        } else {
          if (error.response.status === 422) {
            updatedCustomer.errors = error.response.data;
          } else if (error.response.status == 500) {
            updatedCustomer.errors.base = [error.response.data.message];
            handleAxiosError(error);
          }
        }

        setCustomer(updatedCustomer);
        setStatus(Status.start);
      });
  };

  if (kyccCheckedCount >= kyccMonthlyLimit) {
    return <Alert color="danger">今月の検索上限に到達しています</Alert>;
  } else {
    if (status === Status.start) {
      return (
        <Conditions
          target={target}
          customer={customer}
          onAddCondition={onAddCondition}
          onChangeCondition={onChangeCondition}
          onRemoveCondition={onRemoveCondition}
          onStartCheck={onStartCheck}
        />
      );
    } else if (status === Status.cheking) {
      return <Checking />;
    } else if (status === Status.done) {
      return <Results customer={customer} />;
    } else {
      return null;
    }
  }
}
